import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Plus, Save, Undo2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	getPayrollRunQueryOptions,
	listPayrollWorkersQueryOptions,
	useConfirmPayrollRun,
	useRevertPayrollRun,
	useUpsertPayrollItems,
} from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { centsToDecimalString, toCents } from "@/utils/decimal";
import { formatMoney } from "@/utils/financial";
import { RunStatusBadge } from "../../-components/PayrollBadges";
import { PayrollGate } from "../../-components/PayrollGate";
import {
	itemToRow,
	type PayrollRunFormInput,
	type PayrollRunFormOutput,
	payrollRunSchema,
	toUpsertPayload,
} from "./-components/payrollRunSchema";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/payroll/runs/$runId/",
)({
	component: PayrollRunDetailPage,
});

function PayrollRunDetailPage() {
	const { orgId } = Route.useParams();
	return (
		<PayrollGate orgId={orgId}>
			<RunDetailContent />
		</PayrollGate>
	);
}

function RunDetailContent() {
	const { orgId, runId } = Route.useParams();
	const { t } = useTranslation();
	const { formatDateOnly, formatDateTime, formatDateWithOptions } =
		useDateFormatter();

	const { data: run, isLoading } = useQuery(
		getPayrollRunQueryOptions(orgId, runId),
	);
	const { data: workersData } = useQuery(
		listPayrollWorkersQueryOptions(orgId, {
			activeOnly: "true",
			limit: "200",
		}),
	);

	const upsertMutation = useUpsertPayrollItems();
	const confirmMutation = useConfirmPayrollRun();
	const revertMutation = useRevertPayrollRun();

	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isRevertOpen, setIsRevertOpen] = useState(false);

	const form = useForm<PayrollRunFormInput, unknown, PayrollRunFormOutput>({
		resolver: zodResolver(payrollRunSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: { items: [] },
	});
	const { control, watch, reset } = form;
	const { fields, append } = useFieldArray({ control, name: "items" });

	// Reseed the editable grid whenever the run reloads (id or server revision).
	const runRevision = run ? `${run.id}:${run.updatedAt}` : null;
	// biome-ignore lint/correctness/useExhaustiveDependencies: reseed only on server revision change
	useEffect(() => {
		if (run) reset({ items: run.items.map(itemToRow) });
	}, [runRevision]);

	const guardedSubmit = useGuardedSubmit(form, (values) =>
		upsertMutation.mutateAsync({
			organizationId: orgId,
			runId,
			data: toUpsertPayload(values),
		}),
	);

	if (isLoading || !run) {
		return (
			<div className="flex justify-center py-16">
				<LoadingSpinner />
			</div>
		);
	}

	const isDraft = run.status === "DRAFT";
	const workers = workersData?.data ?? [];
	const items = watch("items");
	const usedWorkerIds = new Set(items.map((r) => r.workerId));
	const availableWorkers = workers.filter((w) => !usedWorkerIds.has(w.id));

	const addWorkerRow = (workerId: string) => {
		const worker = workers.find((w) => w.id === workerId);
		if (!worker) return;
		append({
			workerId: worker.id,
			workerName: worker.name,
			employmentType: worker.employmentType,
			establishmentId: worker.defaultEstablishmentId ?? null,
			daysWorked: "",
			grossAmount:
				worker.employmentType === "MONTHLY" ? (worker.baseSalary ?? "") : "",
			encargosAmount: "",
			netAmount: "",
			notes: "",
		});
	};

	// Display totals — integer-cent sums of the money columns (§3.6), never float.
	const totalsCents = items.reduce(
		(acc, r) => ({
			gross: acc.gross + (toCents(r.grossAmount) ?? 0),
			encargos: acc.encargos + (toCents(r.encargosAmount) ?? 0),
			net: acc.net + (toCents(r.netAmount || r.grossAmount) ?? 0),
		}),
		{ gross: 0, encargos: 0, net: 0 },
	);

	const competencyLabel = formatDateWithOptions(
		new Date(run.competencyMonth).getTime(),
		{ month: "long", year: "numeric", timeZone: "UTC" },
	);

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={run.label || competencyLabel}
				showBackButton
				actions={
					isDraft ? (
						<Button
							onClick={() => setIsConfirmOpen(true)}
							disabled={
								confirmMutation.isPending ||
								upsertMutation.isPending ||
								fields.length === 0
							}
						>
							<CheckCircle2 className="mr-2 h-4 w-4" />
							{t("financial.payroll.runs.confirm.action")}
						</Button>
					) : (
						<Button
							variant="outline"
							onClick={() => setIsRevertOpen(true)}
							disabled={revertMutation.isPending}
						>
							<Undo2 className="mr-2 h-4 w-4" />
							{t("financial.payroll.runs.revert.action")}
						</Button>
					)
				}
			/>

			<Card>
				<CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
					<div className="flex flex-wrap items-center gap-3">
						<RunStatusBadge status={run.status} />
						<span className="text-sm text-base-content/70">
							{competencyLabel}
						</span>
						<span className="text-sm text-base-content/70">
							{t("financial.payroll.runs.columns.paymentDate")}:{" "}
							{formatDateOnly(new Date(run.paymentDate).getTime())}
						</span>
						{run.confirmedAt && (
							<span className="text-sm text-success">
								{t("financial.payroll.runs.confirmedAt")}:{" "}
								{formatDateTime(new Date(run.confirmedAt).getTime())}
							</span>
						)}
					</div>
					<span className="text-lg font-semibold">
						{formatMoney(centsToDecimalString(totalsCents.net))}
					</span>
				</CardContent>
			</Card>

			{isDraft && availableWorkers.length > 0 && (
				<div className="flex items-center gap-2">
					<Select value="" onValueChange={addWorkerRow}>
						<SelectTrigger className="sm:w-72">
							<Plus className="mr-2 h-4 w-4" />
							<SelectValue
								placeholder={t("financial.payroll.runs.addWorker")}
							/>
						</SelectTrigger>
						<SelectContent>
							{availableWorkers.map((w) => (
								<SelectItem key={w.id} value={w.id}>
									{w.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			<Form {...form}>
				<form onSubmit={guardedSubmit} className="flex flex-col gap-6">
					<Card>
						<CardContent className="p-0">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>
											{t("financial.payroll.runs.grid.worker")}
										</TableHead>
										<TableHead className="w-24 text-center hidden sm:table-cell">
											{t("financial.payroll.runs.grid.daysWorked")}
										</TableHead>
										<TableHead className="w-32 text-right">
											{t("financial.payroll.runs.grid.gross")}
										</TableHead>
										<TableHead className="w-32 text-right hidden md:table-cell">
											{t("financial.payroll.runs.grid.encargos")}
										</TableHead>
										<TableHead className="w-32 text-right">
											{t("financial.payroll.runs.grid.net")}
										</TableHead>
										<TableHead className="hidden lg:table-cell">
											{t("financial.payroll.runs.grid.notes")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{fields.length === 0 ? (
										<TableRow>
											<TableCell
												colSpan={6}
												className="py-10 text-center text-sm text-base-content/60"
											>
												{t("financial.payroll.runs.grid.empty")}
											</TableCell>
										</TableRow>
									) : (
										fields.map((row, index) => (
											<TableRow key={row.id}>
												<TableCell>
													<span className="font-medium">{row.workerName}</span>
													<span className="block text-xs text-base-content/60">
														{t(
															`financial.payroll.employmentTypes.${row.employmentType}`,
														)}
													</span>
												</TableCell>
												<TableCell className="hidden sm:table-cell text-center">
													{row.employmentType === "DAILY" ? (
														isDraft ? (
															<FormField
																control={control}
																name={`items.${index}.daysWorked`}
																render={({ field }) => (
																	<FormItem className="gap-1">
																		<FormControl>
																			<NumberField
																				mode="numeric"
																				className="text-center"
																				value={field.value}
																				onChange={field.onChange}
																				onBlur={field.onBlur}
																				name={field.name}
																				ref={field.ref}
																			/>
																		</FormControl>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														) : (
															(items[index]?.daysWorked ?? "—")
														)
													) : (
														"—"
													)}
												</TableCell>
												<TableCell className="text-right">
													{isDraft ? (
														<FormField
															control={control}
															name={`items.${index}.grossAmount`}
															render={({ field }) => (
																<FormItem className="gap-1">
																	<FormControl>
																		<NumberField
																			className="text-right"
																			value={field.value}
																			onChange={field.onChange}
																			onBlur={field.onBlur}
																			name={field.name}
																			ref={field.ref}
																			placeholder="0,00"
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
													) : (
														formatMoney(items[index]?.grossAmount ?? "0")
													)}
												</TableCell>
												<TableCell className="text-right hidden md:table-cell">
													{isDraft ? (
														<FormField
															control={control}
															name={`items.${index}.encargosAmount`}
															render={({ field }) => (
																<FormItem className="gap-1">
																	<FormControl>
																		<NumberField
																			className="text-right"
																			value={field.value ?? ""}
																			onChange={field.onChange}
																			onBlur={field.onBlur}
																			name={field.name}
																			ref={field.ref}
																			placeholder="0,00"
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
													) : items[index]?.encargosAmount ? (
														formatMoney(items[index].encargosAmount)
													) : (
														"—"
													)}
												</TableCell>
												<TableCell className="text-right">
													{isDraft ? (
														<FormField
															control={control}
															name={`items.${index}.netAmount`}
															render={({ field }) => (
																<FormItem className="gap-1">
																	<FormControl>
																		<NumberField
																			className="text-right"
																			value={field.value ?? ""}
																			onChange={field.onChange}
																			onBlur={field.onBlur}
																			name={field.name}
																			ref={field.ref}
																			placeholder={t(
																				"financial.payroll.runs.grid.auto",
																			)}
																		/>
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
													) : (
														<span className="font-medium">
															{formatMoney(items[index]?.netAmount ?? "0")}
														</span>
													)}
												</TableCell>
												<TableCell className="hidden lg:table-cell">
													{isDraft ? (
														<FormField
															control={control}
															name={`items.${index}.notes`}
															render={({ field }) => (
																<FormItem className="gap-1">
																	<FormControl>
																		<Input {...field} />
																	</FormControl>
																	<FormMessage />
																</FormItem>
															)}
														/>
													) : (
														(items[index]?.notes ?? "—")
													)}
												</TableCell>
											</TableRow>
										))
									)}
									{fields.length > 0 && (
										<TableRow className="font-medium">
											<TableCell className="hidden sm:table-cell" />
											<TableCell className="sm:hidden">
												{t("financial.payroll.runs.grid.total")}
											</TableCell>
											<TableCell className="text-right hidden sm:table-cell">
												{t("financial.payroll.runs.grid.total")}
											</TableCell>
											<TableCell className="text-right">
												{formatMoney(centsToDecimalString(totalsCents.gross))}
											</TableCell>
											<TableCell className="text-right hidden md:table-cell">
												{formatMoney(
													centsToDecimalString(totalsCents.encargos),
												)}
											</TableCell>
											<TableCell className="text-right">
												{formatMoney(centsToDecimalString(totalsCents.net))}
											</TableCell>
											<TableCell className="hidden lg:table-cell" />
										</TableRow>
									)}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

					<FormRootMessage />

					{isDraft && fields.length > 0 && (
						<div className="flex items-center justify-end gap-3">
							<SubmitButton pending={upsertMutation.isPending}>
								<Save className="mr-2 h-4 w-4" />
								{t("financial.payroll.runs.grid.save")}
							</SubmitButton>
						</div>
					)}
				</form>
			</Form>

			<AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.payroll.runs.confirm.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.payroll.runs.confirm.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={confirmMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								confirmMutation
									.mutateAsync({ organizationId: orgId, runId })
									.then(() => setIsConfirmOpen(false))
							}
							disabled={confirmMutation.isPending}
						>
							{confirmMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.payroll.runs.confirm.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={isRevertOpen} onOpenChange={setIsRevertOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.payroll.runs.revert.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.payroll.runs.revert.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={revertMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								revertMutation
									.mutateAsync({ organizationId: orgId, runId })
									.then(() => setIsRevertOpen(false))
							}
							disabled={revertMutation.isPending}
						>
							{revertMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.payroll.runs.revert.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
